"use client";

import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { MinusIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@ui/lib/utils";

const inputOTPVariants = cva(["tw-flex tw-items-center", "has-[:disabled]:tw-opacity-50"], {
  variants: {
    size: {
      sm: "tw-gap-2",
      md: "tw-gap-2",
      lg: "tw-gap-3",
    },
  },
  defaultVariants: {
    size: "lg",
  },
});

const inputOTPInputVariants = cva(["disabled:tw-cursor-not-allowed"], {
  variants: {},
  defaultVariants: {},
});

const inputOTPGroupVariants = cva(["tw-flex tw-items-center"], {
  variants: {},
  defaultVariants: {},
});

const inputOTPSlotVariants = cva(
  [
    "tw-relative tw-flex tw-items-center tw-justify-center",
    "tw-border tw-border-black tw-bg-white",
    "tw-font-display tw-font-normal",
    "tw-outline-none tw-transition-all",
  ],
  {
    variants: {
      size: {
        sm: "tw-h-9 tw-w-9 tw-rounded-md tw-text-sm tw-px-2 tw-py-1",
        md: "tw-h-10 tw-w-10 tw-rounded-lg tw-text-base tw-px-3 tw-py-1.5",
        lg: "tw-h-11 tw-w-11 tw-rounded-xl tw-text-base tw-px-4 tw-py-1.5",
      },
      active: {
        true: "tw-z-10 tw-outline tw-outline-4 tw-outline-offset-0 tw-outline-gray-300",
        false: "",
      },
      invalid: {
        true: "tw-border-gray-800",
        false: "",
      },
    },
    compoundVariants: [
      {
        active: true,
        invalid: true,
        className: "tw-border-gray-800 tw-outline-gray-800/20",
      },
    ],
    defaultVariants: {
      size: "lg",
      active: false,
      invalid: false,
    },
  },
);

const inputOTPPlaceholderVariants = cva(["tw-text-gray-300 tw-font-display tw-font-normal"], {
  variants: {
    size: {
      sm: "tw-text-sm",
      md: "tw-text-base",
      lg: "tw-text-base",
    },
  },
  defaultVariants: {
    size: "lg",
  },
});

const inputOTPCaretVariants = cva([
  "tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center",
]);

const inputOTPCaretBlinkVariants = cva(["tw-w-px tw-bg-black tw-animate-pulse tw-duration-1000"], {
  variants: {
    size: {
      sm: "tw-h-4",
      md: "tw-h-5",
      lg: "tw-h-5",
    },
  },
  defaultVariants: {
    size: "lg",
  },
});

const inputOTPSeparatorVariants = cva([], {
  variants: {},
  defaultVariants: {},
});

type OTPSize = "sm" | "md" | "lg";

function InputOTP({
  className,
  containerClassName,
  slotSize = "lg",
  ...props
}: React.ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
  slotSize?: OTPSize;
}) {
  return (
    <OTPInput
      data-slot='input-otp'
      data-slot-size={slotSize}
      containerClassName={cn(inputOTPVariants({ size: slotSize }), containerClassName)}
      className={cn(inputOTPInputVariants(), className)}
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot='input-otp-group' className={cn(inputOTPGroupVariants(), className)} {...props} />;
}

function InputOTPSlot({
  index,
  className,
  size,
  active,
  invalid,
  placeholder = "0",
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof inputOTPSlotVariants> & {
    index: number;
    placeholder?: string;
  }) {
  const inputOTPContext = React.useContext(OTPInputContext);
  const slot = inputOTPContext?.slots[index];
  const { char, hasFakeCaret, isActive } = slot ?? {};

  // Use context values if props not explicitly provided
  const resolvedActive = active ?? isActive ?? false;
  const resolvedSize = size ?? "lg";

  return (
    <div
      data-slot='input-otp-slot'
      data-active={resolvedActive}
      className={cn(inputOTPSlotVariants({ size: resolvedSize, active: resolvedActive, invalid }), className)}
      {...props}
    >
      {char ? (
        <span className='tw-text-black'>{char}</span>
      ) : (
        <span className={inputOTPPlaceholderVariants({ size: resolvedSize })}>{placeholder}</span>
      )}
      {hasFakeCaret && (
        <div className={inputOTPCaretVariants()}>
          <div className={inputOTPCaretBlinkVariants({ size: resolvedSize })} />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot='input-otp-separator'
      role='separator'
      className={cn(inputOTPSeparatorVariants(), className)}
      {...props}
    >
      <MinusIcon />
    </div>
  );
}

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
export type { OTPSize };
export {
  inputOTPVariants,
  inputOTPInputVariants,
  inputOTPGroupVariants,
  inputOTPSlotVariants,
  inputOTPPlaceholderVariants,
  inputOTPCaretVariants,
  inputOTPCaretBlinkVariants,
  inputOTPSeparatorVariants,
};
